import { Routes } from '@angular/router';
import { permission } from '../user-permissions/route-permission';
import { WorkspaceDrawerBehaviorResolver } from '../layout/workspace-drawer-behavior-resolver';
import { routeData, routeParams } from '../shared/route-helpers';
import { SampleTrackingLevelComponent } from './sample-tracking-level.component';
import { SampleTrackingGuard } from './sample-tracking-guard';
import { SampleTrackingNavigationComponent } from './navigation/sample-tracking-navigation.component';
import { STWorkspaceLevelLayoutComponent } from './home/st-workspace-level-layout.component';
import { SampleTrackingDashboardComponent } from './dashboard/sample-tracking-dashboard.component';
import { SampleTrackingPreferencesComponent } from './user-preferences/sample-tracking-preferences.component';
import { VisitOverviewComponent } from './tracking-overview/visit-overview/visit-overview.component';
import { SampleOverviewComponent } from './tracking-overview/sample-overview/sample-overview.component';
import { OverviewDashboardComponent } from './dashboard/overview-dashboard/overview-dashboard.component';
import { SampleTrackingFilterSamplesComponent } from './filter-samples/st-filter-samples.component';
import { SampleTrackingReportsComponent } from './reports/sample-tracking-reports.component';
import { ViewGeneratedReportsComponent } from './reports/view-reports/generated-reports/view-generated-reports.component';
import { ViewScheduledReportsComponent } from './reports/view-reports/scheduled-reports/view-scheduled-reports.component';
import { SampleTrackingReportsLoaderComponent } from './reports/sample-tracking-report-loader';
import { NewReportComponent } from './reports/new-report/new-report.component';
import { UserAudienceRoleGuard, audienceRole } from '../user/user-audience-role-guard';
import { SubjectSearchOverviewComponent } from './tracking-overview/subject-search-overview/subject-search-overview.component';
import { SubjectVisitDetailsComponent } from './tracking-overview/subject-details/subject-visit-details.component';
import { SampleMonitoringComponent } from './sample-monitoring/sample-monitoring.component';
import { SampleMonitoringNotificationsComponent } from './sample-monitoring/sample-monitoring-notifications/sample-monitoring-notifications.component';
import { SampleMonitoringNotificationRulesComponent } from './sample-monitoring/sm-notification-rules/sm-notification-rules.component';
import { SMNotificationRuleFormComponent } from './sample-monitoring/notification-form/sm-notification-rule-form.component';
import { DynamicListViewComponent } from './filter-samples/dynamic-list-view.component';

export const sampleTrackingRoutes: Routes = [
    {
        path: 'sampletracking',
        canActivate: [SampleTrackingGuard],
        component: SampleTrackingLevelComponent,
        resolve: {
            [routeData.drawerBehavior]: WorkspaceDrawerBehaviorResolver,
        },
        children: [
            {
                path: '',
                component: STWorkspaceLevelLayoutComponent,
                children: [
                    {
                        path: 'home',
                        canActivate: [UserAudienceRoleGuard],
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Sample Tracking'
                        },
                        component: SampleTrackingDashboardComponent,
                    },
                    {
                        path: 'preference',
                        pathMatch: 'full',
                        canActivate: [UserAudienceRoleGuard],
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'My Preferences'
                        },
                        component: SampleTrackingPreferencesComponent,
                    },
                    {
                        path: 'samples/:studyGroupId/:type',
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Sample Tracking Dashboard'
                        },
                        pathMatch: 'full',
                        canActivate: [UserAudienceRoleGuard],
                        component: OverviewDashboardComponent
                    },
                    {
                        path: 'samples/:studyGroupId/:type/filter-samples/SamplesOnHold',
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Filter-Samples'
                        },
                        canActivate: [UserAudienceRoleGuard],
                        component: SampleTrackingFilterSamplesComponent
                    },
                    {
                        path: 'samples/:studyGroupId/:type/filter-samples/:list',
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Filter-Samples'
                        },
                        canActivate: [UserAudienceRoleGuard],
                        component: DynamicListViewComponent
                    },
                    {
                        path: 'tracking/visit',
                        canActivate: [UserAudienceRoleGuard],
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Visit Tracking Details'
                        },
                        component: VisitOverviewComponent,
                    },
                    {
                        path: 'tracking/sample/:id',
                        canActivate: [UserAudienceRoleGuard],
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Sample Tracking Details'
                        },
                        component: SampleOverviewComponent,
                    },
                    {
                        path: 'reports',
                        canActivate: [UserAudienceRoleGuard],
                        data: {
                            ...audienceRole('Sponsor'),
                        },
                        component: SampleTrackingReportsLoaderComponent,
                        children: [
                            {
                                path: 'overview',
                                canActivate: [UserAudienceRoleGuard],
                                data: {
                                    ...audienceRole('Sponsor'),
                                    title: 'Reports'
                                },
                                component: SampleTrackingReportsComponent,
                            },
                            {
                                path: 'view/generated-reports',
                                canActivate: [UserAudienceRoleGuard],
                                data: {
                                    ...audienceRole('Sponsor'),
                                    title: 'Generated Reports'
                                },
                                component: ViewGeneratedReportsComponent,
                            },
                            {
                                path: 'view/scheduled-reports',
                                canActivate: [UserAudienceRoleGuard],
                                data: {
                                    ...audienceRole('Sponsor'),
                                    title: 'Scheduled Reports'
                                },
                                component: ViewScheduledReportsComponent,
                            },
                            {
                                path: 'new-report/:reportTypeId/:reportOption',
                                canActivate: [UserAudienceRoleGuard],
                                data: {
                                    ...audienceRole('Sponsor'),
                                    title: 'Generate New Report'
                                },
                                component: NewReportComponent,
                            }
                        ]
                    },
                    {
                        path: 'sample-monitoring',
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Sample Monitoring'
                        },
                        component: SampleMonitoringComponent,
                        children: [
                            {
                                path: 'notifications',
                                data: {
                                    ...audienceRole('Sponsor'),
                                    title: 'Sample Monitoring Notifications'
                                },
                                component: SampleMonitoringNotificationsComponent
                            },
                            {
                                path: 'notification/rules',
                                data: {
                                    ...audienceRole('Sponsor'),
                                    title: 'Sample Monitoring Notification Rules'
                                },
                                component: SampleMonitoringNotificationRulesComponent
                            },
                        ]
                    },
                    {
                        path: 'subject/:id/search',
                        canActivate: [UserAudienceRoleGuard],
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Patient Search Results'
                        },
                        component: SubjectSearchOverviewComponent,
                    },
                    {
                        path: 'subject/:id/visit/list',
                        canActivate: [UserAudienceRoleGuard],
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Patient Visit Details'
                        },
                        component: SubjectVisitDetailsComponent,
                    },
                    {
                        path: 'sample-monitoring/new/rule',
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Sample Monitoring Settings.'
                        },
                        component: SMNotificationRuleFormComponent
                    },
                    {
                        path: 'sample-monitoring/rule/:id',
                        data: {
                            ...audienceRole('Sponsor'),
                            title: 'Sample Monitoring Settings.'
                        },
                        component: SMNotificationRuleFormComponent
                    },
                    {
                        path: '',
                        component: SampleTrackingNavigationComponent,
                        outlet: 'navigation'
                    },
                    {
                        path: '',
                        redirectTo: 'home',
                        pathMatch: 'full'
                    },
                ]
            }
        ]
    }
];
